// ============================================================================
// THE PHRASING LAYER for the Connections screen.
//
// Every string the mockups show as a time ("3 days ago · Aug 21, 6:12pm",
// "recorded yesterday 2:10pm", "unused for 83 days", "1h 46m audio") is DERIVED
// here from a timestamp and the app clock — never stored (README §State
// Management → "Derived, never stored"). Nothing in this file reads the wall
// clock: `today` and `nowMinutes` come from `useClock()`, which is fixed in demo
// mode, so every render is reproducible and the screenshot pair never drifts.
// ============================================================================

import { daysBetween, formatShortDate, type ISODate } from '@app/shared'

import { clockFromTimestamp } from '../../lib/time'

/** Calendar days between a timestamp's day and today. Positive = in the past. */
export function daysAgoOf(timestamp: string, today: ISODate): number {
  return daysBetween(timestamp.slice(0, 10) as ISODate, today)
}

/** Whole minutes from a timestamp to now. Positive = in the past. */
export function minutesAgoOf(timestamp: string, today: ISODate, nowMinutes: number): number {
  const at = Number(timestamp.slice(11, 13)) * 60 + Number(timestamp.slice(14, 16))
  return daysAgoOf(timestamp, today) * 1440 + nowMinutes - at
}

/**
 * '41 min ago' · '2 hours ago' · '3 days ago'.
 *
 * DAYS ARE COUNTED AS CALENDAR DAYS, not 24-hour blocks. A sync that stopped at
 * 6pm three evenings ago is "3 days ago" all of the next day, which is how the
 * owner counts it and what the mockup says; dividing elapsed minutes by 1440
 * would render "2 days ago" for most of that day and quietly understate the
 * outage. Never round an outage towards being shorter than it is.
 */
export function agoPhrase(timestamp: string, today: ISODate, nowMinutes: number): string {
  const days = daysAgoOf(timestamp, today)
  const minutes = minutesAgoOf(timestamp, today, nowMinutes)
  if (minutes < 1) return 'just now'
  if (days === 0) {
    if (minutes < 60) return `${minutes} min ago`
    const hours = Math.floor(minutes / 60)
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`
  }
  return `${days} ${days === 1 ? 'day' : 'days'} ago`
}

/** 'Sep 13' — the calendar day a timestamp fell on. */
export function formatShortDateOf(timestamp: string): string {
  return formatShortDate(timestamp.slice(0, 10) as ISODate)
}

/** 'Sep 13, 6:12pm' — the exact stamp an outage needs beside its relative age. */
export function stampPhrase(timestamp: string): string {
  return `${formatShortDateOf(timestamp)}, ${clockFromTimestamp(timestamp)}`
}

/** 'today 7:02am' · 'yesterday 11:48pm' · 'Jun 25 10:14am'. */
export function dayClockPhrase(timestamp: string, today: ISODate): string {
  const days = daysAgoOf(timestamp, today)
  const day = days === 0 ? 'today' : days === 1 ? 'yesterday' : formatShortDateOf(timestamp)
  return `${day} ${clockFromTimestamp(timestamp)}`
}

/** '41 min' — audio length in whole minutes, the unit every job row uses. */
export function minutesPhrase(seconds: number): string {
  return `${Math.round(seconds / 60)} min`
}

/** '4h 22m' · '46m' — a pile of audio, not one clip. */
export function spanPhrase(seconds: number): string {
  const total = Math.round(seconds / 60)
  const h = Math.floor(total / 60)
  const m = total % 60
  return h === 0 ? `${m}m` : `${h}h ${m}m`
}

/** '3m 12s' — how long one job actually took. */
export function jobDurationPhrase(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}m ${String(s).padStart(2, '0')}s`
}

/** '2,340' — the index count, with the separator the mockup uses. */
export function countPhrase(n: number): string {
  return n.toLocaleString('en-US')
}

/**
 * '18.4 GB' · '240 MB'. Decimal units, because that is what a disk reports and
 * what the mockup shows; no silent switch to GiB halfway up the scale.
 */
export function bytesPhrase(bytes: number): string {
  if (bytes >= 1_000_000_000) {
    const gb = bytes / 1_000_000_000
    return `${gb >= 100 ? Math.round(gb) : Number(gb.toFixed(1))} GB`
  }
  return `${Math.round(bytes / 1_000_000)} MB`
}
